/*
 * Draft 10 in the click-through preview: Insights (numbers, forecast, Ask) and Books (accounts, transactions, rules,
 * reconcile, profit and loss, balance sheet, 1099s), with made-up data kept in memory. Not The Cleaners' real numbers.
 */
import {
  DIRECT_GROUPS,
  isOwed,
  pairCode,
  pairsFor,
  STANDARD_CATEGORIES,
  addDays,
  buildPlanEnds,
  line1099Cents,
  payeeGuess,
  payeeKey,
  ruleFor,
  type Action,
  type BankAccountRow,
  type CategoryRow,
  type CompanyStatsRow,
  type TaxReview,
  type ForecastAnswer,
  type InsightsAnswer,
  type CancelsAnswer,
  type MarketingCostRow,
  type PlanEndsAnswer,
  type PnlAnswer,
  type ReconcileRow,
  type RuleRow,
  type TransactionRow,
} from "@steward/shared";

type Answer = Promise<{ status: number; body: unknown }>;
const ok = (body: unknown): Answer => Promise.resolve({ status: 200, body });
const NO = (): Answer =>
  Promise.resolve({
    status: 403,
    body: { code: "not_allowed", message: "Your role can't do that. Ask the owner if you need it." },
  });
const fail = (status: number, code: string, message: string): Answer => Promise.resolve({ status, body: { code, message } });

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
let n = 0;
const id = () => `00000000-0000-4000-8000-${String(900000000000 + (n += 1))}`;
type Json = Record<string, unknown>;

// Insights ------------------------------------------------------------------------------------------------------------

const costs: MarketingCostRow[] = [];

function insights(from: string, to: string): InsightsAnswer {
  const months: InsightsAnswer["months"] = [];
  for (let m = from.slice(0, 7), i = 0; m <= to.slice(0, 7) && i < 24; i++) {
    const seed = Number(m.slice(5, 7));
    months.push({
      month: m,
      billedCents: 1_840_000 + seed * 41_000,
      collectedCents: 1_760_000 + seed * 39_500,
      refundsCents: seed % 3 === 0 ? -12_000 : 0,
      newClients: 9 + (seed % 4),
      visitsDone: 118 + seed * 2,
    });
    const [y, mm] = m.split("-").map(Number) as [number, number];
    m = mm === 12 ? `${String(y + 1)}-01` : `${String(y)}-${String(mm + 1).padStart(2, "0")}`;
  }
  const sum = (k: "billedCents" | "collectedCents" | "refundsCents" | "newClients") => months.reduce((a, x) => a + x[k], 0);
  const cost = (c: string) =>
    costs
      .filter((x) => x.channel === c && x.month >= from.slice(0, 7) && x.month <= to.slice(0, 7))
      .reduce((a, x) => a + x.amountCents, 0);
  const channel = (c: string, share: number, clients: number) => {
    const k = cost(c);
    return {
      channel: c,
      newClients: clients,
      collectedCents: Math.round(sum("collectedCents") * share),
      costCents: k,
      costPerClientCents: k && clients ? Math.round(k / clients) : null,
    };
  };
  const newClients = sum("newClients");
  return {
    from,
    to,
    money: {
      billedCents: sum("billedCents"),
      collectedCents: sum("collectedCents"),
      refundsCents: sum("refundsCents"),
      openCents: 412_500,
      pastDueCents: 86_000,
    },
    months,
    clients: {
      newClients,
      repeatClients: 141,
      planClients: 118,
      lost: 6,
      lostReasons: [
        { reason: "moved", count: 3 },
        { reason: "elsewhere", count: 2 },
        { reason: "not_needed", count: 1 },
      ],
      churnPercent: 4,
    },
    quotes: { sent: 64, won: 29, lost: 21, winPercent: 45 },
    channels: [
      channel("google", 0.46, Math.round(newClients * 0.45)),
      channel("referral", 0.24, Math.round(newClients * 0.2)),
      channel("lsa", 0.14, Math.round(newClients * 0.18)),
      channel("facebook", 0.09, Math.round(newClients * 0.12)),
      channel("Not set", 0.07, Math.round(newClients * 0.05)),
    ],
    jobCosts: [
      {
        jobId: id(),
        name: "Move out cleaning",
        clientName: "Morgan K.",
        visits: 1,
        billedCents: 32_000,
        laborCents: 22_400,
        profitCents: 9_600,
        marginPercent: 30,
        noCrew: 0,
        recurring: false,
      },
      {
        jobId: id(),
        name: "Standard Cleaning",
        clientName: "Taylor B.",
        visits: 6,
        billedCents: 81_000,
        laborCents: 40_500,
        profitCents: 40_500,
        marginPercent: 50,
        noCrew: 0,
        recurring: true,
      },
      {
        jobId: id(),
        name: "Deep Cleaning",
        clientName: "Jordan P.",
        visits: 2,
        billedCents: 52_000,
        laborCents: 23_400,
        profitCents: 28_600,
        marginPercent: 55,
        noCrew: 1,
        recurring: false,
      },
      {
        jobId: id(),
        name: "Office cleaning",
        clientName: "Bayou Dental",
        visits: 12,
        billedCents: 240_000,
        laborCents: 96_000,
        profitCents: 144_000,
        marginPercent: 60,
        noCrew: 0,
        recurring: true,
      },
    ],
    payments: {
      byMethod: [
        { method: "card", cents: Math.round(sum("collectedCents") * 0.81), count: 412 },
        { method: "check", cents: Math.round(sum("collectedCents") * 0.12), count: 38 },
        { method: "cash", cents: Math.round(sum("collectedCents") * 0.07), count: 41 },
      ],
      failed: 2,
    },
    charts: demoCharts(from, to, sum("billedCents"), sum("collectedCents"), newClients),
  };
}

function demoCharts(from: string, to: string, billed: number, collected: number, newClients: number): InsightsAnswer["charts"] {
  const days = Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1;
  const prevTo = addDays(from, -1);
  const year = Number(today().slice(0, 4));
  const years: InsightsAnswer["charts"]["years"] = [];
  for (let y = year - 1; y <= year; y++) {
    for (let mm = 1; mm <= 12; mm++) {
      const month = `${String(y)}-${String(mm).padStart(2, "0")}`;
      if (month > today().slice(0, 7)) break;
      const season = [0.7, 0.8, 1, 1.1, 1.2, 1.25, 1.15, 1.1, 1.05, 1, 0.85, 0.9][mm - 1] ?? 1;
      years.push({ month, collectedCents: Math.round((y === year ? 1_900_000 : 1_650_000) * season) });
    }
  }
  const quoteWeeks: InsightsAnswer["charts"]["quoteWeeks"] = [];
  for (let d = from, i = 0; d <= to && i < 60; d = addDays(d, 7), i++) {
    const sent = 3 + ((i * 7) % 5);
    const won = Math.max(1, Math.round(sent * (0.35 + (i % 3) * 0.1)));
    quoteWeeks.push({ week: d, sent, sentCents: sent * 26_500, won, wonCents: won * 24_000 });
  }
  return {
    previous: {
      from: addDays(prevTo, -(days - 1)),
      to: prevTo,
      billedCents: Math.round(billed * 0.92),
      collectedCents: Math.round(collected * 1.06),
      newClients: Math.round(newClients * 0.88),
      visitsDone: 340,
      quotesSent: 58,
      quotesWon: 31,
      leads: 70,
    },
    years,
    leads: { created: 78, quoted: 52, won: 29 },
    quoteWeeks,
    owing: {
      clients: 14,
      top: [
        { clientId: id(), name: "Bayou Dental", balanceCents: 150_000, lateCents: 120_000 },
        { clientId: id(), name: "Morgan K.", balanceCents: 70_000, lateCents: 70_000 },
        { clientId: id(), name: "Front Street Lofts", balanceCents: 48_000, lateCents: 0 },
        { clientId: id(), name: "Taylor B.", balanceCents: 30_000, lateCents: 30_000 },
      ],
    },
    payDays: 4,
  };
}

/** Made-up plans for Why plans ended: 14 clients, some still going, some stopped early after a cleaner change. */
function planEnds(): PlanEndsAnswer {
  const t = today();
  const names = ["Ana B.", "Kenia R.", "Rosa M."];
  const freq = ["biweekly", "monthly", "weekly"];
  const reasons = ["moved", "elsewhere", "not_needed", null];
  const clients = [];
  const jobs = [];
  const visits = [];
  for (let i = 0; i < 14; i++) {
    const id = `demo-plan-${String(i)}`;
    const stopped = i % 3 !== 0;
    clients.push({
      id,
      name:
        ["Maya Long", "Tom Reed", "Jill Park", "Omar Diaz", "Lena Ford", "Ruth Cole", "Sam Hart"][i % 7]! +
        (i >= 7 ? " Jr." : ""),
      active: !stopped,
      inactiveReason: stopped ? (reasons[i % 4] ?? null) : null,
      inactiveNote: null,
    });
    const frequency = freq[i % 3]!;
    const start = addDays(t, -40 - i * 25);
    jobs.push({ id, clientId: id, frequency, startDate: start, endsOn: null, active: !stopped });
    const step = frequency === "weekly" ? 7 : frequency === "biweekly" ? 14 : 30;
    const count = stopped ? 2 + (i % 5) : Math.floor((40 + i * 25) / step);
    for (let v = 0; v < count && addDays(start, v * step) <= t; v++)
      visits.push({
        jobId: id,
        date: addDays(start, v * step),
        crew: [names[(i + (stopped && v > 0 ? v : 0)) % 3]!],
        minutes: v % 2 ? 150 : 0,
        baseCents: 16000,
      });
  }
  return buildPlanEnds({ clients, jobs, visits, today: t });
}

const forecast = (): ForecastAnswer => ({
  weeks: Array.from({ length: 8 }, (_, w) => ({
    start: addDays(today(), w * 7),
    visits: 26 + ((w * 3) % 7),
    bookedCents: 410_000 + ((w * 37_000) % 160_000),
  })),
  windows: [30, 60, 90].map((days) => {
    const booked = days * 61_000;
    const invoices = days === 30 ? 412_500 : 412_500;
    const payroll = Math.round(booked * 0.45);
    return {
      days,
      visits: Math.round(days * 4.1),
      bookedCents: booked,
      invoicesCents: invoices,
      payrollCents: payroll,
      netCents: booked + invoices - payroll,
    };
  }),
  quotes: { openCents: 418_000, winPercent: 45, likelyCents: 188_100 },
  payrollHow: "runs",
});

const asked: { id: string; question: string; answer: string; at: string }[] = [];

export function insightsRoute(
  gate: (a: Action) => boolean,
  companies: { id: string; name: string }[],
  method: string,
  p: string,
  q: URLSearchParams,
  b: unknown,
): Answer | null {
  const body = (b ?? {}) as Json;
  if (!p.startsWith("insights") && p !== "ask") return null;
  if (p === "insights/companies") {
    if (!gate("report.children")) return NO();
    return ok(
      companies.map((c, i): CompanyStatsRow => ({
        companyId: c.id,
        name: c.name,
        billedCents: 1_200_000 + i * 310_000,
        collectedCents: 1_150_000 + i * 300_000,
        newClients: 7 + i,
        visitsDone: 90 + i * 11,
      })),
    );
  }
  if (!gate("insights.read")) return NO();
  const t = today();
  if (p === "insights") return ok(insights(q.get("from") ?? addDays(t, -90), q.get("to") ?? t));
  if (p === "insights/forecast") return ok(forecast());
  if (p === "insights/plan-ends") return ok(planEnds());
  if (p === "insights/cancels") return ok(demoCancels(q.get("from") ?? addDays(t, -90), q.get("to") ?? t));
  if (p === "insights/costs" && method === "GET") return ok({ costs });
  if (p === "insights/costs" && method === "PUT") {
    const row = {
      id: id(),
      month: String(body.month),
      channel: String(body.channel),
      amountCents: Number(body.amountCents),
      note: null,
    };
    const at = costs.findIndex((c) => c.month === row.month && c.channel === row.channel);
    if (at >= 0) costs.splice(at, 1);
    costs.push(row);
    return ok(row);
  }
  if (p.startsWith("insights/costs/") && method === "DELETE") {
    const at = costs.findIndex((c) => c.id === p.split("/")[2]);
    if (at >= 0) costs.splice(at, 1);
    return ok({ ok: true });
  }
  if (p === "ask" && method === "GET") return ok({ asked });
  if (p === "ask" && method === "POST") {
    const question = typeof body.question === "string" ? body.question : "";
    const a = {
      id: id(),
      question,
      answer:
        "This is the preview, so the answer is made up.\nIn the real app, the Steward looks up your numbers and answers here. For example, you collected $54,120 in the last 3 months, and Google brought the most new clients.",
      at: new Date().toISOString(),
    };
    asked.unshift(a);
    return ok(a);
  }
  return null;
}

// Books ---------------------------------------------------------------------------------------------------------------

const categories: CategoryRow[] = [
  {
    id: id(),
    code: "open",
    name: "Uncategorized",
    type: "open",
    group: "Uncategorized",
    taxLine: null,
    parentId: null,
    hidden: false,
  },
  ...STANDARD_CATEGORIES.map((c) => ({
    id: id(),
    code: c.code,
    name: c.name,
    type: c.type,
    group: c.group,
    taxLine: c.taxLine,
    parentId: null,
    hidden: false,
  })),
];
const catId = (code: string) => categories.find((c) => c.code === code)!.id;
const accounts: BankAccountRow[] = [
  {
    id: id(),
    name: "Chase checking",
    kind: "checking",
    personal: false,
    last4: "4410",
    openingBalanceCents: 1_250_000,
    openingDay: "2025-12-31",
    linked: false,
    reconciledThrough: null,
    bankBalanceCents: null,
    bankBalanceAt: null,
    booksBalanceCents: null,
    postedBalanceCents: null,
    pending: 0,
    lastSyncAt: null,
    check: null,
  },
  {
    id: id(),
    name: "Amex Business",
    kind: "card",
    personal: false,
    last4: "1009",
    openingBalanceCents: -84_000,
    openingDay: "2025-12-31",
    linked: false,
    reconciledThrough: null,
    bankBalanceCents: null,
    bankBalanceAt: null,
    booksBalanceCents: null,
    postedBalanceCents: null,
    pending: 0,
    lastSyncAt: null,
    check: null,
  },
  {
    id: id(),
    name: "Chase savings",
    kind: "savings",
    personal: false,
    last4: "8029",
    openingBalanceCents: 300_000,
    openingDay: "2025-12-31",
    linked: false,
    reconciledThrough: null,
    bankBalanceCents: null,
    bankBalanceAt: null,
    booksBalanceCents: null,
    postedBalanceCents: null,
    pending: 0,
    lastSyncAt: null,
    check: null,
  },
  {
    id: id(),
    name: "Owner's Visa",
    kind: "card",
    personal: true,
    last4: "7816",
    openingBalanceCents: 0,
    openingDay: "2025-12-31",
    linked: false,
    reconciledThrough: null,
    bankBalanceCents: null,
    bankBalanceAt: null,
    booksBalanceCents: null,
    postedBalanceCents: null,
    pending: 0,
    lastSyncAt: null,
    check: null,
  },
];
// The checking account is linked: the bank's balance shows next to Books' (a little off, as when a line is missing).
Object.assign(accounts[0]!, { linked: true, lastSyncAt: new Date(Date.now() - 15 * 3_600_000).toISOString() });
const rules: RuleRow[] = [
  { id: id(), match: "SHELL", categoryId: catId("fuel"), payee: null, direction: "out", accountId: null },
  { id: id(), match: "HOME DEPOT", categoryId: catId("supplies"), payee: null, direction: null, accountId: null },
];
const txs: TransactionRow[] = [];
const reconciles: ReconcileRow[] = [];
function addTx(
  day: string,
  description: string,
  amountCents: number,
  account = 0,
  code: string | null = null,
  extra: Partial<TransactionRow> = {},
) {
  const rule = code ? null : ruleFor(rules, description);
  const row: TransactionRow = {
    id: id(),
    accountId: accounts[account]!.id,
    day,
    amountCents,
    description,
    categoryId: code ? catId(code) : (rule?.categoryId ?? null),
    auto: Boolean(rule),
    split: null,
    payee: null,
    note: null,
    source: "csv",
    matched: false,
    reconciled: false,
    importId: null,
    receipts: 0,
    reviewed: Boolean(code),
    excluded: false,
    pairId: null,
    matchedPayments: [],
    pairsFound: 0,
    paymentsFound: 0,
    ai: null,
    ...extra,
  };
  txs.push(row);
  return row;
}
/** Both sides of a transfer between our accounts, paired and posted. */
function addPair(day: string, from: number, to: number, cents: number, code: string, words: [string, string]) {
  const a = addTx(day, words[0], -cents, from, code);
  const b = addTx(day, words[1], cents, to, code);
  a.pairId = b.id;
  b.pairId = a.id;
}
/** A check deposit in the preview's made-up payments, to show Match. */
const DEMO_PAYMENT = { id: "00000000-0000-4000-8000-00000000c043", label: "Check #1043 from Rivera Family, invoice #1188" };
{
  const y = today().slice(0, 4);
  for (let m = 1; m <= Number(today().slice(5, 7)); m++) {
    const d = (day: number) => `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    addTx(d(3), "STRIPE TRANSFER", 1_480_000 + m * 9_000, 0, "sales");
    addTx(d(5), "SHELL OIL 57442", -18_420);
    addTx(d(8), "HOME DEPOT #3512", -31_260);
    addTx(d(10), `Zelle payment to Kenia R JPM99${String(m)}`, -62_000, 0, "contract");
    addTx(d(10), `Zelle payment to Kevin S JPM98${String(m)}`, -58_000, 0, "contract");
    addTx(d(15), "GOOGLE *ADS", -45_000, 1, "ads");
    addTx(d(18), "PROGRESSIVE INS", -21_300, 0, "insurance");
    addTx(d(20), "QUO TEXTING", -6_900, 1, "software");
    const now = m === Number(today().slice(5, 7));
    if (!now) addPair(d(25), 0, 1, 51_900, "cardpay", ["AMEX EPAYMENT ACH PMT", "PAYMENT RECEIVED THANK YOU"]);
    if (now) {
      addTx(d(1), "AMAZON MKTPL", -8_745, 1);
      addTx(d(2), "AMEX EPAYMENT ACH PMT", -51_900, 0);
      addTx(d(3), "PAYMENT RECEIVED THANK YOU", 51_900, 1);
      addTx(d(4), "ONLINE TRANSFER TO SAV 8029", -50_000, 0);
      addTx(d(4), "ONLINE TRANSFER FROM CHK 4410", 50_000, 2);
      addTx(d(6), "MOBILE DEPOSIT", 32_500, 0);
      addTx(d(7), "ONLINE TRANSFER GMR", 2_797, 0, null, { payee: "Gabriel R" });
      addTx(d(7), "TARGET 0412", -6_412, 3);
      addTx(d(8), "PAYMENT TO VISA 7816", -40_000, 0);
    }
  }
}
// Every line not posted yet is pending; the bank holds a little more than Books has, so the card shows the gap.
for (const t of txs) if (t.categoryId && t.day < today().slice(0, 8) + "01") t.reviewed = true;
const booksBalance = (a: BankAccountRow, day = today()) =>
  a.openingBalanceCents +
  txs.filter((t) => t.accountId === a.id && !t.excluded && t.day <= day).reduce((s, t) => s + t.amountCents, 0);
accounts[0]!.bankBalanceCents = booksBalance(accounts[0]!) + 2_797;
accounts[0]!.bankBalanceAt = accounts[0]!.lastSyncAt;

function pnl(from: string, to: string): PnlAnswer {
  const list: string[] = [];
  for (let m = from.slice(0, 7); m <= to.slice(0, 7);) {
    list.push(m);
    const [y, mm] = m.split("-").map(Number) as [number, number];
    m = mm === 12 ? `${String(y + 1)}-01` : `${String(y)}-${String(mm + 1).padStart(2, "0")}`;
  }
  const inSpan = txs.filter((t) => t.day >= from && t.day <= to);
  const rows: PnlAnswer["rows"] = [];
  let income = 0;
  let direct = 0;
  let expense = 0;
  for (const c of categories) {
    if (c.type !== "income" && c.type !== "expense" && c.type !== "asset") continue;
    const byMonth = list.map(() => 0);
    for (const t of inSpan) {
      const parts = t.split ?? (t.categoryId ? [{ categoryId: t.categoryId, amountCents: t.amountCents }] : []);
      for (const part of parts)
        if (part.categoryId === c.id)
          byMonth[list.indexOf(t.day.slice(0, 7))]! += c.type === "income" ? part.amountCents : -part.amountCents;
    }
    const totalCents = byMonth.reduce((a, x) => a + x, 0);
    if (!totalCents) continue;
    rows.push({ categoryId: c.id, name: c.name, type: c.type, group: c.group, taxLine: c.taxLine, byMonth, totalCents });
    if (c.type === "income") income += totalCents;
    else if (c.type === "expense") {
      expense += totalCents;
      if (DIRECT_GROUPS.includes(c.group)) direct += totalCents;
    }
  }
  const open = inSpan.filter((t) => !t.categoryId && !t.split);
  return {
    from,
    to,
    months: list,
    rows,
    incomeCents: income,
    directCents: direct,
    grossCents: income - direct,
    expenseCents: expense,
    profitCents: income - expense,
    boughtToKeepCents: 0,
    uncategorizedCents: open.reduce((a, t) => a + t.amountCents, 0),
    uncategorizedCount: open.length,
  };
}

let lockDay: string | null = null;
let taxReview: TaxReview | null = null;
/** Lines that could be a side of a transfer: in the books, not paired. */
const openSides = () => txs.filter((t) => !t.excluded && !t.pairId);
const demoPayments = (t: TransactionRow) =>
  t.amountCents === 32_500 && !t.matchedPayments.length && !txs.some((x) => x.matchedPayments.includes(DEMO_PAYMENT.id))
    ? [DEMO_PAYMENT]
    : [];
const setup = () => ({
  accounts: accounts.map((a) => ({
    ...a,
    booksBalanceCents: booksBalance(a),
    postedBalanceCents:
      a.openingBalanceCents +
      txs.filter((t) => t.accountId === a.id && !t.excluded && t.reviewed).reduce((x, t) => x + t.amountCents, 0),
    pending: txs.filter((t) => t.accountId === a.id && !t.reviewed && !t.excluded).length,
  })),
  categories,
  rules,
  connections: [],
  plaidReady: false,
  years: [Number(today().slice(0, 4))],
  lockDay,
});

export function booksRoute(
  gate: (a: Action) => boolean,
  method: string,
  p: string,
  q: URLSearchParams,
  b: unknown,
): Answer | null {
  if (p !== "books" && !p.startsWith("books/")) return null;
  if (!gate(method === "GET" ? "books.read" : "books.update")) return NO();
  const body = (b ?? {}) as Json;
  const route = `${method} ${p}`;
  if (route === "GET books") return ok(setup());
  if (route === "POST books/transactions/ai-review") {
    // Made-up AI picks (the real ones come from Claude, api books/ai-review.ts).
    const picks: [RegExp, string | null, string, boolean, "transfer" | "personal" | null, string | null][] = [
      [/AMAZON/, "supplies", "Amazon is often job supplies, but could be office", false, null, "Amazon"],
      [/SHELL|EXXON|CHEVRON/, "fuel", "Gas station, fuel for the work truck", true, null, "Shell"],
      [/MOBILE DEPOSIT/, "sales", "A check you deposited, most likely a client paying", false, null, null],
      [/TRANSFER GMR/, "otherinc", "Small money in from Gabriel R, maybe paying you back", false, null, "Gabriel R"],
      [/PAYMENT TO VISA|EPAYMENT|THANK YOU|TRANSFER (TO|FROM)/, null, "Money between your accounts", false, "transfer", null],
      [/TARGET/, "draw", "Target is usually personal shopping", false, "personal", "Target"],
    ];
    let reviewed = 0;
    let sure = 0;
    for (const t of txs) {
      if (t.reviewed || t.excluded || t.pairId || t.auto || t.split || (t.ai && !body.again)) continue;
      if (body.accountId && t.accountId !== body.accountId) continue;
      const hit = picks.find(([re]) => re.test(t.description));
      if (!hit) continue;
      const [, code, why, isSure, hint, payee] = hit;
      // Like the server: no personal accounts, and never a cost on money in or income on money out.
      if (accounts.find((x) => x.id === t.accountId)?.personal) continue;
      const kind = code ? categories.find((c) => c.code === code)?.type : null;
      const fits = kind && !(t.amountCents > 0 && kind === "expense") && !(t.amountCents < 0 && kind === "income");
      if (code && !fits) continue;
      if (code) t.categoryId = catId(code);
      if (payee && !t.payee) t.payee = payee;
      // Like the server with books.aiPosts on: a sure pick is posted by the AI.
      t.ai = { why, sure: isSure, hint, tax: null, posted: isSure };
      if (isSure) t.reviewed = true;
      reviewed++;
      if (isSure) sure++;
    }
    return ok({ reviewed, sure, left: 0, posted: sure, paired: 0 });
  }
  if (route === "GET books/tax") return ok({ review: taxReview });
  if (route === "POST books/tax/review") {
    taxReview = {
      id: "00000000-0000-4000-8000-0000000000aa",
      year: typeof body.year === "number" ? body.year : new Date().getFullYear(),
      at: new Date().toISOString(),
      summary: "Made up for the preview. Profit is up from last year, so a few moves before Dec 31 can lower the bill.",
      moves: [
        {
          title: "Write off the new mower this year",
          why: "Section 179 lets you take the whole cost now instead of over 5 years",
          saves: "$300 to $500",
          how: "Keep it on Equipment bought to keep and tell your tax pro to take Section 179",
          when: "When you file",
          askPro: false,
        },
        {
          title: "Look at an S corp",
          why: "With profit over about $50,000, paying yourself a salary can cut self employment tax",
          saves: "$2,000 to $5,000",
          how: "Ask your tax pro to run the numbers before you file Form 2553",
          when: "By March 15 for next year",
          askPro: true,
        },
      ],
      fixes: [{ what: "Roblox $10.99", why: "Looks personal, move it to Owner draw" }],
      dates: [{ day: "Jan 15", what: "Last quarterly payment for this year" }],
      sources: [],
    };
    return ok({ review: taxReview });
  }
  if (
    route === "POST books/transactions/post" ||
    route === "POST books/transactions/exclude" ||
    route === "POST books/transactions/include"
  ) {
    const ids = new Set((body.ids as string[] | undefined) ?? []);
    let changed = 0;
    for (const t of txs) {
      if (!ids.has(t.id)) continue;
      if (p.endsWith("post") && (t.categoryId || t.split) && !t.reviewed) t.reviewed = true;
      else if (p.endsWith("exclude") && !t.excluded && !t.pairId) t.excluded = true;
      else if (p.endsWith("include") && t.excluded) Object.assign(t, { excluded: false, reviewed: false });
      else continue;
      changed++;
    }
    return ok({ changed, skipped: ids.size - changed });
  }
  if (p.startsWith("books/transactions/") && p.endsWith("/matches")) {
    const t = txs.find((x) => x.id === p.split("/")[2]);
    if (!t) return fail(404, "not_found", "That transaction was not found.");
    const self = accounts.find((a) => a.id === t.accountId)!;
    return ok({
      pairs: t.pairId
        ? []
        : pairsFor(t, openSides()).map((o) => {
            const other = accounts.find((a) => a.id === o.accountId)!;
            return {
              id: o.id,
              accountId: o.accountId,
              accountName: other.name,
              day: o.day,
              amountCents: o.amountCents,
              description: o.description,
              categoryName: categories.find(
                (c) => c.code === pairCode({ kind: self.kind, personal: self.personal, amountCents: t.amountCents }, other),
              )!.name,
            };
          }),
      payments: demoPayments(t).map((x) => ({ ids: [x.id], day: t.day, amountCents: t.amountCents, label: x.label })),
      stripe: false,
    });
  }
  if (p.startsWith("books/transactions/") && p.endsWith("/pair")) {
    const t = txs.find((x) => x.id === p.split("/")[2]);
    const o = txs.find((x) => x.id === body.otherId);
    if (!t || !o) return fail(404, "not_found", "That transaction was not found.");
    const [st, so] = [accounts.find((a) => a.id === t.accountId)!, accounts.find((a) => a.id === o.accountId)!];
    for (const [x, self, other] of [
      [t, st, so],
      [o, so, st],
    ] as const)
      Object.assign(x, {
        pairId: x === t ? o.id : t.id,
        categoryId: catId(pairCode({ kind: self.kind, personal: self.personal, amountCents: x.amountCents }, other)),
        auto: false,
        reviewed: true,
        excluded: self.personal && !other.personal,
      });
    return ok(t);
  }
  if (p.startsWith("books/transactions/") && p.endsWith("/unpair")) {
    const t = txs.find((x) => x.id === p.split("/")[2]);
    const o = txs.find((x) => x.id === t?.pairId);
    for (const x of [t, o]) if (x) Object.assign(x, { pairId: null, categoryId: null, reviewed: false, excluded: false });
    return ok(t);
  }
  if (p.startsWith("books/transactions/") && p.endsWith("/match")) {
    const t = txs.find((x) => x.id === p.split("/")[2]);
    if (!t) return fail(404, "not_found", "That transaction was not found.");
    Object.assign(t, { matchedPayments: body.paymentIds, categoryId: catId("sales"), reviewed: true, auto: false });
    return ok(t);
  }
  if (p.startsWith("books/transactions/") && p.endsWith("/unmatch")) {
    const t = txs.find((x) => x.id === p.split("/")[2]);
    if (t) Object.assign(t, { matchedPayments: [], categoryId: null, reviewed: false });
    return ok(t);
  }
  if (route === "GET books/transactions") {
    const year = q.get("year") ?? today().slice(0, 4);
    const from = q.get("from") ?? `${year}-01-01`;
    const to = q.get("to") ?? `${year}-12-31`;
    const acct = q.get("accountId");
    const cat = q.get("categoryId");
    const state = q.get("state");
    const sides = openSides();
    return ok({
      transactions: txs
        .filter(
          (t) =>
            (state === "excluded" ? t.excluded : !t.excluded) &&
            (state !== "pending" || !t.reviewed) &&
            (state !== "posted" || t.reviewed) &&
            t.day >= from &&
            t.day <= to &&
            (!acct || t.accountId === acct) &&
            (!cat || t.categoryId === cat || (t.split ?? []).some((p) => p.categoryId === cat)) &&
            (!q.get("uncategorized") || (!t.categoryId && !t.split)),
        )
        .sort((x, y) => y.day.localeCompare(x.day))
        .map((t) =>
          state === "pending" && !t.pairId && !t.matchedPayments.length
            ? { ...t, pairsFound: pairsFor(t, sides).length, paymentsFound: demoPayments(t).length }
            : t,
        ),
    });
  }
  if (route === "GET books/ledger") {
    const from = q.get("from") ?? "";
    const to = q.get("to") ?? "";
    const cat = q.get("categoryId");
    const lines = txs
      .filter((t) => t.day >= from && t.day <= to)
      .flatMap((t) =>
        (t.split ?? (t.categoryId ? [{ categoryId: t.categoryId, amountCents: t.amountCents }] : [])).map((p, i) => ({
          id: `${t.id.slice(0, -2)}${String(10 + i)}`,
          day: t.day,
          accountId: p.categoryId,
          amountCents: -p.amountCents,
          sourceType: "bank_tx",
          sourceId: t.id,
          description: t.description,
          payee: t.payee,
          bankAccountId: t.accountId,
        })),
      )
      .filter((l) => !cat || l.accountId === cat)
      .sort((a, b) => a.day.localeCompare(b.day));
    return ok({ lines, more: false });
  }
  if (route === "GET books/reconciles") return ok({ reconciles });
  if (route === "PUT books/lock") {
    lockDay = (body.day as string | null | undefined) ?? null;
    return ok({ lockDay });
  }
  if (p.startsWith("books/transactions/") && p.endsWith("/receipts")) {
    if (method === "GET") return ok({ files: [] });
    return fail(409, "preview", "Receipts are kept in the real app.");
  }
  if (p.startsWith("books/transactions/") && method === "PATCH") {
    const t = txs.find((x) => x.id === p.split("/")[2]);
    if (!t) return fail(404, "not_found", "That transaction was not found.");
    if (Array.isArray(body.split)) Object.assign(t, { split: body.split, categoryId: null, auto: false, reviewed: true });
    else if (body.categoryId !== undefined)
      Object.assign(t, { categoryId: body.categoryId, auto: false, split: null, reviewed: Boolean(body.categoryId) });
    if (typeof body.note === "string") t.note = body.note || null;
    if (typeof body.payee === "string") t.payee = body.payee || null;
    return ok(t);
  }
  if (route === "POST books/transactions/bulk") {
    const ids = new Set((body.ids as string[] | undefined) ?? []);
    let changed = 0;
    for (const t of txs)
      if (ids.has(t.id)) {
        Object.assign(t, { categoryId: body.categoryId ?? null, auto: false, reviewed: Boolean(body.categoryId) });
        changed++;
      }
    return ok({ changed });
  }
  if (p.startsWith("books/transactions/") && method === "DELETE") {
    const t = txs.find((x) => x.id === p.split("/")[2]);
    if (t) t.excluded = true;
    return ok({ ok: true });
  }
  if (route === "POST books/accounts") {
    const a = {
      id: id(),
      linked: false,
      reconciledThrough: null,
      last4: null,
      openingDay: null,
      openingBalanceCents: 0,
      personal: false,
      bankBalanceCents: null,
      bankBalanceAt: null,
      booksBalanceCents: null,
      postedBalanceCents: null,
      pending: 0,
      lastSyncAt: null,
      ...body,
    } as BankAccountRow;
    accounts.push(a);
    return Promise.resolve({ status: 201, body: a });
  }
  if (p.startsWith("books/accounts/") && method === "PATCH") {
    const a = accounts.find((x) => x.id === p.split("/")[2]);
    if (a) Object.assign(a, body);
    return ok(a);
  }
  if (route === "POST books/rules" || (p.startsWith("books/rules/") && method === "PATCH")) {
    if (method === "PATCH") {
      const at = rules.findIndex((x) => x.id === p.split("/")[2]);
      if (at >= 0) rules.splice(at, 1);
    }
    const r: RuleRow = {
      id: id(),
      match: String(body.match),
      categoryId: String(body.categoryId),
      payee: (body.payee as string | undefined) ?? null,
      direction: (body.direction as RuleRow["direction"] | undefined) ?? null,
      accountId: (body.accountId as string | null | undefined) ?? null,
    };
    rules.unshift(r);
    let applied = 0;
    for (const t of txs)
      if (!t.categoryId && ruleFor([r], t.description)) {
        Object.assign(t, { categoryId: r.categoryId, auto: true });
        applied++;
      }
    return Promise.resolve({ status: 201, body: { rule: r, applied } });
  }
  if (p.startsWith("books/rules/") && method === "DELETE") {
    const at = rules.findIndex((x) => x.id === p.split("/")[2]);
    if (at >= 0) rules.splice(at, 1);
    return ok({ ok: true });
  }
  if (route === "POST books/categories") {
    const c: CategoryRow = {
      id: id(),
      code: "own",
      name: String(body.name),
      type: (body.type as CategoryRow["type"] | undefined) ?? "expense",
      group: "Overhead",
      taxLine: "27a",
      parentId: (body.parentId as string | undefined) ?? null,
      hidden: false,
    };
    categories.push(c);
    return Promise.resolve({ status: 201, body: c });
  }
  if (p.startsWith("books/categories/") && method === "PATCH") {
    const c = categories.find((x) => x.id === p.split("/")[2]);
    if (c) c.hidden = Boolean(body.hidden);
    return ok({ ok: true });
  }
  if (route === "POST books/import") {
    const lines = (typeof body.text === "string" ? body.text : "").split(/\r?\n/).length - 1;
    return ok({ importId: null, added: 0, skipped: Math.max(0, lines), bad: 0, ruled: 0, locked: 0 });
  }
  if (route === "POST books/payouts/split") return fail(409, "no_stripe", "Connect Stripe in Settings, Keys first.");
  if (route === "POST books/reconcile/look") {
    const a = accounts.find((x) => x.id === body.accountId);
    if (!a?.openingDay) return fail(409, "no_opening", "Set this account's opening balance first, in Accounts.");
    const end = String(body.endDay);
    return ok({
      beginCents: a.openingBalanceCents,
      from: a.openingDay,
      first: true,
      transactions: txs.filter((t) => t.accountId === a.id && t.day > a.openingDay! && t.day <= end && !t.reconciled),
    });
  }
  if (route === "POST books/reconcile") {
    const ids = new Set((body.ids as string[] | undefined) ?? []);
    for (const t of txs) if (ids.has(t.id)) t.reconciled = true;
    const a = accounts.find((x) => x.id === body.accountId);
    if (a) a.reconciledThrough = String(body.endDay);
    for (const r of reconciles) if (r.accountId === body.accountId) r.latest = false;
    reconciles.unshift({
      id: id(),
      auto: false,
      accountId: String(body.accountId),
      endDay: String(body.endDay),
      beginCents: a?.openingBalanceCents ?? 0,
      endCents: Number(body.endBalanceCents),
      count: ids.size,
      undone: false,
      latest: true,
      createdAt: new Date().toISOString(),
    });
    return Promise.resolve({ status: 201, body: { id: id() } });
  }
  if (route === "POST books/categories/quickbooks") return ok({ renamed: 0, added: 0 });
  if (route === "GET books/cash-flow") {
    // Like the server (books/reports.ts cashFlow): lines in checking, savings and cash, by category, plus is money in.
    const from = q.get("from") ?? `${today().slice(0, 4)}-01-01`;
    const to = q.get("to") ?? today();
    const list: string[] = [];
    for (let m = from.slice(0, 7); m <= to.slice(0, 7);) {
      list.push(m);
      const [y, mm] = m.split("-").map(Number) as [number, number];
      m = mm === 12 ? `${String(y + 1)}-01` : `${String(y)}-${String(mm + 1).padStart(2, "0")}`;
    }
    const cashIds = new Set(accounts.filter((x) => !x.personal && !isOwed(x.kind)).map((x) => x.id));
    const rows = new Map<string, number[]>();
    for (const t of txs) {
      if (t.excluded || !cashIds.has(t.accountId) || t.day < from || t.day > to) continue;
      const k = t.categoryId ?? "";
      const arr = rows.get(k) ?? list.map(() => 0);
      arr[list.indexOf(t.day.slice(0, 7))]! += t.amountCents;
      rows.set(k, arr);
    }
    const keyOf = (cat: CategoryRow | undefined, total: number) =>
      !cat
        ? total >= 0
          ? "in"
          : "out"
        : cat.type === "income"
          ? "in"
          : cat.type === "expense"
            ? "out"
            : cat.type === "asset"
              ? "kept"
              : "owner";
    const sections = (["in", "out", "kept", "owner"] as const).map((key) => {
      const these = [...rows]
        .map(([k, byMonth]) => ({
          cat: categories.find((c) => c.id === k),
          byMonth,
          totalCents: byMonth.reduce((x, y) => x + y, 0),
        }))
        .filter((r) => keyOf(r.cat, r.totalCents) === key)
        .map((r) => ({
          categoryId: r.cat?.id ?? null,
          name: r.cat?.name ?? "No category yet",
          byMonth: r.byMonth,
          totalCents: r.totalCents,
        }));
      const byMonth = list.map((_, i) => these.reduce((x, r) => x + (r.byMonth[i] ?? 0), 0));
      return { key, rows: these, byMonth, totalCents: byMonth.reduce((x, y) => x + y, 0) };
    });
    const netByMonth = list.map((_, i) => sections.reduce((x, r) => x + (r.byMonth[i] ?? 0), 0));
    const start = accounts.filter((x) => cashIds.has(x.id)).reduce((x, y) => x + y.openingBalanceCents, 0);
    const startByMonth: number[] = [];
    const endByMonth: number[] = [];
    let run = start;
    for (const n of netByMonth) {
      startByMonth.push(run);
      run += n;
      endByMonth.push(run);
    }
    return ok({
      from,
      to,
      months: list,
      sections: sections.filter((x) => x.rows.length),
      netByMonth,
      netCents: run - start,
      startByMonth,
      endByMonth,
      startCents: start,
      endCents: run,
    });
  }
  if (route === "GET books/pnl") return ok(pnl(q.get("from") ?? `${today().slice(0, 4)}-01-01`, q.get("to") ?? today()));
  if (route === "GET books/balance") {
    const day = q.get("day") ?? today();
    const bal = accounts.map((a) => ({
      id: a.id,
      name: a.name,
      kind: a.kind,
      personal: a.personal,
      balanceCents: booksBalance(a, day),
    }));
    const cash = bal.filter((a) => !a.personal && !isOwed(a.kind)).reduce((s, a) => s + a.balanceCents, 0);
    const owed = -bal.filter((a) => !a.personal && isOwed(a.kind)).reduce((s, a) => s + a.balanceCents, 0);
    return ok({
      day,
      accounts: bal,
      cashCents: cash,
      cardsOwedCents: owed,
      boughtToKeepCents: 0,
      loansOwedCents: 0,
      ownerInCents: 0,
      ownerOutCents: 0,
      equityCents: cash - owed,
    });
  }
  if (route === "GET books/1099") {
    const year = Number(q.get("year") ?? today().slice(0, 4));
    const by = new Map<string, { name: string; totalCents: number; count: number }>();
    for (const t of txs.filter((x) => x.categoryId === catId("contract") && x.day.startsWith(String(year)))) {
      const name = t.payee ?? payeeGuess(t.description);
      const r = by.get(payeeKey(name)) ?? { name, totalCents: 0, count: 0 };
      r.totalCents -= t.amountCents;
      r.count++;
      by.set(payeeKey(name), r);
    }
    const line = line1099Cents(year);
    return ok({
      year,
      lineCents: line,
      payees: [...by].map(([key, r]) => ({
        key,
        ...r,
        reaches: r.totalCents >= line,
        w9: key.startsWith("kenia"),
        personId: null,
        personName: null,
      })),
    });
  }
  if (p.startsWith("books/1099/")) return ok({ ok: true });
  if (route === "GET books/adjustments") return ok({ adjustments: [] });
  if (route === "POST books/adjustments") return Promise.resolve({ status: 201, body: { id: id() } });
  if (route === "POST books/plaid/link-token")
    return fail(409, "no_plaid", "Head office saves Plaid's keys in Settings, Keys first.");
  return null;
}

/** Made-up cancels for the preview: the same shape the server sends. */
function demoCancels(from: string, to: string): CancelsAnswer {
  const info = (reason: CancelsAnswer["list"][number]["info"]["reason"], by: "client" | "us" | null, hours: number, fee = 0) => ({
    reason,
    note: null,
    by,
    hoursBefore: hours,
    late: hours < 24,
    feeCents: fee || null,
    at: null,
    savedBy: "Halfi",
  });
  const list: CancelsAnswer["list"] = [
    {
      ref: "demo-c1",
      date: addDays(to, -3),
      clientId: "00000000-0000-4000-8000-000000000101",
      clientName: "Dana Price",
      jobName: "Biweekly cleaning",
      status: "cancelled",
      info: info("client_away", "client", 30),
    },
    {
      ref: "demo-c2",
      date: addDays(to, -9),
      clientId: "00000000-0000-4000-8000-000000000102",
      clientName: "Marcus Hill",
      jobName: "Monthly cleaning",
      status: "cancelled",
      info: info("client_skip", "client", 6, 4000),
    },
    {
      ref: "demo-c3",
      date: addDays(to, -16),
      clientId: "00000000-0000-4000-8000-000000000101",
      clientName: "Dana Price",
      jobName: "Biweekly cleaning",
      status: "skipped",
      info: info("not_given", null, 50),
    },
    {
      ref: "demo-c4",
      date: addDays(to, -24),
      clientId: "00000000-0000-4000-8000-000000000103",
      clientName: "Lena Roy",
      jobName: "Weekly cleaning",
      status: "cancelled",
      info: info("no_crew", "us", 14),
    },
  ];
  return {
    from,
    to,
    totals: { done: 46, cancelled: 3, skipped: 1, late: 2, lateWithFee: 1, feeCents: 4000, rate: 8 },
    months: [{ month: to.slice(0, 7), done: 46, cancelled: 3, skipped: 1, late: 2, rate: 8 }],
    reasons: [
      { reason: "client_away", off: 1, late: 0 },
      { reason: "client_skip", off: 1, late: 1 },
      { reason: "no_crew", off: 1, late: 1 },
      { reason: "not_given", off: 1, late: 0 },
    ],
    by: [
      { by: "client", off: 2, late: 1 },
      { by: "us", off: 1, late: 1 },
      { by: "unknown", off: 1, late: 0 },
    ],
    clients: [
      {
        clientId: "00000000-0000-4000-8000-000000000101",
        name: "Dana Price",
        done: 4,
        off: 2,
        late: 0,
        lastReason: "client_away",
      },
      {
        clientId: "00000000-0000-4000-8000-000000000102",
        name: "Marcus Hill",
        done: 2,
        off: 1,
        late: 1,
        lastReason: "client_skip",
      },
      { clientId: "00000000-0000-4000-8000-000000000103", name: "Lena Roy", done: 5, off: 1, late: 1, lastReason: "no_crew" },
    ],
    list,
  };
}
